import { Link } from 'react-router-dom';
import { Check, Star, ArrowRight, Phone, Flame } from 'lucide-react';
import { PACKAGES, ADDONS, CONTACT, PREMIUM_KEYS, packagePrice } from '../data';

export default function Pricing() {
  return (
    <div data-testid="pricing-page" className="pt-32 pb-24 bg-[#0a0a0a] text-white min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <span className="pill mb-4">Pricing</span>
        <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl tracking-tighter max-w-3xl">Straightforward. Honest. Premium.</h1>
        <p className="text-zinc-400 max-w-2xl mt-5 text-lg">Flat starting rates. Pick the package that fits your vehicle.</p>

        <div className="grid lg:grid-cols-2 gap-6 mt-16">
          {Object.entries(PACKAGES).filter(([, p]) => p.category === 'Full Detail').map(([key, p]) => {
            const sedan = packagePrice(key, 'sedan');
            const truck = packagePrice(key, 'truck');
            const suv3row = packagePrice(key, 'suv3row');
            const isPromo = PREMIUM_KEYS.has(key);
            return (
              <div
                key={key}
                data-testid={`pricing-${key}`}
                className={`relative rounded-3xl border p-10 ${isPromo ? 'border-blue-500/40 bg-gradient-to-br from-blue-600/10 to-transparent shadow-[0_0_40px_rgba(37,99,235,0.15)]' : 'border-white/10 bg-[#141414]'}`}
              >
                {isPromo && (
                  <div className="absolute top-6 right-6 bg-gradient-to-r from-amber-500 to-red-500 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
                    <Flame className="w-3.5 h-3.5" /> Limited Time – 20% OFF <Flame className="w-3.5 h-3.5" />
                  </div>
                )}
                {!isPromo && key === 'regular' && <span className="absolute top-6 right-6 pill">Most Affordable</span>}
                <h2 className="font-display text-3xl md:text-4xl font-medium mb-1 mt-4">{p.name}</h2>
                <p className="text-zinc-400 mb-8">{p.tagline}</p>

                <div className="flex items-baseline gap-3 mb-2 flex-wrap">
                  <span className="font-display text-6xl font-light">${sedan.discounted}</span>
                  {sedan.promo && <span className="text-zinc-500 line-through text-2xl">${sedan.original}</span>}
                  <span className="text-zinc-500 text-sm">Sedan / Coupe</span>
                </div>
                <div className="text-zinc-400 text-sm mb-1">
                  ${truck.discounted}{truck.promo && <span className="text-zinc-600 line-through ml-1">${truck.original}</span>} starting · Truck / SUV
                </div>
                <div className="text-zinc-400 text-sm mb-8">
                  ${suv3row.discounted}{suv3row.promo && <span className="text-zinc-600 line-through ml-1">${suv3row.original}</span>} starting · 3-Row SUV / Minivan
                </div>

                <ul className="space-y-3 mb-10">
                  {p.includes.map((inc) => (
                    <li key={inc} className="flex items-start gap-3 text-sm text-zinc-300">
                      <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" /> {inc}
                    </li>
                  ))}
                </ul>

                <Link to={`/book?pkg=${key}`} data-testid={`pricing-book-${key}`} className={`inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-medium transition-all w-full justify-center ${isPromo ? 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow-[0_0_30px_rgba(37,99,235,0.4)]' : 'border border-white/20 hover:bg-white/10 text-white'}`}>
                  Book {p.name} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            );
          })}
        </div>

        {/* FLEET CTA */}
        <div data-testid="pricing-fleet" className="mt-12 rounded-3xl border border-blue-500/30 bg-gradient-to-br from-blue-600/10 to-[#141414] p-8 md:p-10 flex flex-col md:flex-row gap-6 items-center justify-between">
          <div className="max-w-xl">
            <span className="pill mb-3">Fleet Detailing</span>
            <h3 className="font-display text-2xl md:text-3xl font-medium mb-2">Business fleet? Contact us for a custom quote.</h3>
            <p className="text-zinc-400 text-sm">Company cars, work trucks, service vans, semi trucks, and boats. Volume-based pricing tailored to your fleet.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href={`tel:${CONTACT.fleetPhoneRaw}`} data-testid="pricing-fleet-call" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-medium inline-flex items-center gap-2"><Phone className="w-4 h-4" /> Call</a>
            <a href={`sms:${CONTACT.fleetPhoneRaw}`} data-testid="pricing-fleet-text" className="border border-white/20 hover:bg-white/10 text-white px-6 py-3 rounded-full font-medium">Text {CONTACT.fleetPhone}</a>
          </div>
        </div>

        {/* ADDONS */}
        <div className="mt-20">
          <h3 className="font-display text-2xl font-medium mb-6">Add-Ons</h3>
          <div className="bg-[#141414] border border-white/5 rounded-2xl divide-y divide-white/5">
            {ADDONS.map((a) => (
              <div key={a.id} className="flex justify-between items-center px-6 py-4">
                <span className="text-zinc-200">{a.label}</span>
                <span className="text-blue-400 font-medium">{a.price}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-zinc-500 text-sm mt-12 max-w-3xl">
          * Prices are starting rates and may vary depending on vehicle size, condition, and level of contamination.
        </p>
      </div>
    </div>
  );
}
